import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './GitFileViewer.css';

const GitFileViewer = ({ repoUrl, onFileSelect, expanded = true }) => {
  const [fileStructure, setFileStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  useEffect(() => {
    if (repoUrl) {
      fetchRepoStructure(repoUrl);
    }
  }, [repoUrl]);

  const fetchRepoStructure = async (url) => {
    if (!url) return;

    setLoading(true);
    setError(null);

    try {
      // Extract owner and repo from GitHub URL
      const repoInfo = parseGitHubUrl(url);
      if (!repoInfo) {
        throw new Error('Invalid GitHub repository URL');
      }

      const apiGatewayUrl = process.env.REACT_APP_GITHUB_API_GATEWAY_URL || 'http://localhost:3001';
      const response = await fetch(`${apiGatewayUrl}/api/github/repo-structure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baseUrl: repoInfo.baseUrl,
          owner: repoInfo.owner,
          repo: repoInfo.repo,
          path: '' // Root path
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch repository structure: ${response.statusText}`);
      }

      const data = await response.json();
      setFileStructure(data);
      
      // Auto-expand root level
      if (expanded) {
        setExpandedFolders(new Set(['root']));
      }
    } catch (err) {
      console.error('Error fetching repo structure:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const parseGitHubUrl = (url) => {
    try {
      // Handle various GitHub URL formats including GitHub Enterprise
      const patterns = [
        // GitHub Enterprise URLs: https://git.company.com/owner/repo
        /(?:https?:\/\/)?([^\/\s]+)\/([^\/\s]+)\/([^\/\s]+?)(?:\.git)?(?:\/.*)?$/,
        // Standard GitHub URLs: https://github.com/owner/repo
        /(?:https?:\/\/)?(?:www\.)?github\.com\/([^\/\s]+)\/([^\/\s]+?)(?:\.git)?(?:\/.*)?$/,
        // SSH URLs: git@github.com:owner/repo.git
        /git@([^:]+):([^\/\s]+)\/([^\/\s]+?)(?:\.git)?$/,
        // Simple format: owner/repo
        /^([^\/\s]+)\/([^\/\s]+)$/
      ];

      for (let i = 0; i < patterns.length; i++) {
        const pattern = patterns[i];
        const match = url.trim().match(pattern);
        
        if (match) {
          let baseUrl, owner, repo;
          
          if (i === 0) {
            // GitHub Enterprise format: https://git.company.com/owner/repo
            baseUrl = match[1];
            owner = match[2];
            repo = match[3];
          } else if (i === 1) {
            // Standard GitHub format: https://github.com/owner/repo
            baseUrl = 'github.com';
            owner = match[1];
            repo = match[2];
          } else if (i === 2) {
            // SSH format: git@host:owner/repo.git
            baseUrl = match[1];
            owner = match[2];
            repo = match[3];
          } else {
            // Simple format: owner/repo (assumes github.com)
            baseUrl = 'github.com';
            owner = match[1];
            repo = match[2];
          }
          
          // Clean up repo name
          repo = repo.replace(/\.git$/, '').replace(/\/$/, '');
          
          return { 
            baseUrl: baseUrl,
            owner: owner, 
            repo: repo 
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error parsing GitHub URL:', error);
      return null;
    }
  };

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const handleFileClick = (file) => {
    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  const renderFileStructure = (items, basePath = '') => {
    if (!items || !Array.isArray(items)) return null;

    return (
      <ul className="git-file-list">
        {items.map((item, index) => {
          const itemPath = basePath ? `${basePath}/${item.name}` : item.name;
          const isFolder = item.type === 'dir';
          const isExpanded = expandedFolders.has(itemPath);

          return (
            <li key={`${itemPath}-${index}`} className="git-file-item">
              <div 
                className={`git-file-row ${isFolder ? 'folder' : 'file'}`}
                onClick={() => isFolder ? toggleFolder(itemPath) : handleFileClick(item)}
              >
                <span className="git-file-icon">
                  {isFolder ? (
                    <i className={`bi bi-folder${isExpanded ? '-open' : ''}`}></i>
                  ) : (
                    <i className="bi bi-file-earmark"></i>
                  )}
                </span>
                <span className="git-file-name">{item.name}</span>
                {isFolder && (
                  <span className={`git-folder-toggle ${isExpanded ? 'expanded' : ''}`}>
                    <i className="bi bi-chevron-right"></i>
                  </span>
                )}
              </div>
              
              {isFolder && isExpanded && item.children && (
                <div className="git-subfolder">
                  {renderFileStructure(item.children, itemPath)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="git-file-viewer loading">
        <div className="loading-spinner">
          <i className="bi bi-arrow-clockwise spin"></i>
          <span>Loading repository structure...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="git-file-viewer error">
        <div className="error-message">
          <i className="bi bi-exclamation-triangle"></i>
          <span>{error}</span>
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={() => fetchRepoStructure(repoUrl)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!fileStructure) {
    return (
      <div className="git-file-viewer empty">
        <div className="empty-message">
          <i className="bi bi-folder2-open"></i>
          <span>Enter a Git repository URL to view file structure</span>
        </div>
      </div>
    );
  }

  return (
    <div className="git-file-viewer">
      <div className="git-file-header">
        <i className="bi bi-github"></i>
        <span>Repository Structure</span>
      </div>
      <div className="git-file-content">
        {renderFileStructure(fileStructure)}
      </div>
    </div>
  );
};

GitFileViewer.propTypes = {
  repoUrl: PropTypes.string,
  onFileSelect: PropTypes.func,
  expanded: PropTypes.bool,
};

export default GitFileViewer;
